import { T } from 'src/shared/helpers';
import {
    AttributePreviewAction,
    AttributePreviewItems,
    AttributePreviewOption,
    ComposerAttributeNode,
    ComposerAttributeNodeLink,
    ComposerAttributeOptionNode,
    ComposerAttributePreviewType,
    ComposerAttributeType,
    ComposerComparisonOperator,
    ComposerExtensionFieldNode,
    ComposerLogicalOperator,
    ComposerOptionActionNode,
    ComposerOptionActionsType,
    ComposerPricingNode,
    ComposerTemplateNode,
    ComposerTemplateSideRestrictions,
    GetActionTypeFromAiType,
    GetActionTypeFromValue,
    GetActionValueFromType
} from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { ComposerAreaNode } from './../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { getAreaPath, getAreaSVG, getParentGuid, getParentIDNumeric, getParentType, guidIsEmpty, randomCode, tryParsePlatformValue } from './composerHelpers';
import { restoreComposerPlaceholderAreas } from './composerPlaceholderText';
import { getComposerAreaSideID } from './composerAreaIdentity';

export class ComposerAttributeNodeAdapter {
    static cloneData<T>(data: T | null | undefined): T | undefined {
        return data == null ? undefined : JSON.parse(JSON.stringify(data));
    }

    static hydrate<T>(model: T, data: any): T {
        const snapshot = ComposerAttributeNodeAdapter.cloneData(data);
        if (snapshot) for (const [key, value] of Object.entries(snapshot)) (model as any).set(key, value);
        return model;
    }

    // Backbone collections de-duplicate models by their idAttribute. Giving every unsaved
    // entity the traditional -1 id therefore made later siblings replace earlier ones before
    // the mutation was even built. Derive a stable negative id from the entity GUID instead.
    static temporaryNumericID(guid: string): number {
        let hash = 2166136261;
        for (let index = 0; index < guid.length; index++) {
            hash ^= guid.charCodeAt(index);
            hash = Math.imul(hash, 16777619);
        }
        return -(hash >>> 0 || 1);
    }

    static saveNumericID(serverID: number | null | undefined, guid: string): number {
        return serverID != null && serverID > 0 ? serverID : ComposerAttributeNodeAdapter.temporaryNumericID(guid);
    }

    static saveAttributes(attributes: ComposerAttributeNode[]): {
        attributes: MPlaza.Attributes;
        groups: MPlaza.AttributeGroups;
        steps: MPlaza.AttributeSteps;
        templateGroups: MPlaza.TemplateGroups;
    } {
        const resultAttributes = new MPlaza.Attributes();
        const resultGroups = new MPlaza.AttributeGroups();
        const resultSteps = new MPlaza.AttributeSteps();
        const templateGroups = new MPlaza.TemplateGroups();

        attributes.forEach((attribute) => {
            if (attribute.type === ComposerAttributeType.Attribute) {
                resultAttributes.push(ComposerAttributeNodeAdapter.fromNode(attribute));
            } else if (attribute.type === ComposerAttributeType.AttributeGroup) {
                resultGroups.push(ComposerAttributeNodeAdapter.fromGroupNode(attribute));
            } else if (attribute.type === ComposerAttributeType.AttributeStep) {
                resultSteps.push(ComposerAttributeNodeAdapter.fromStepNode(attribute));
            } else if (attribute.type === ComposerAttributeType.PreSetContainer) {
                templateGroups.push(ComposerAttributeNodeAdapter.fromPresetNode(attribute));
            }
        });
        return { attributes: resultAttributes, groups: resultGroups, steps: resultSteps, templateGroups };
    }

    static toPresetNode(group: MPlaza.TemplateGroup): ComposerAttributeNode {
        const node = ComposerAttributeNodeAdapter.toNode(ComposerAttributeNodeAdapter.createNew());
        const groupGuid = group.get('groupGuid');
        const stepGuid = group.get('stepGuid');
        return {
            ...node,
            id: group.get('templateGroupGuid'),
            idNumeric: group.get('templateGroupID') ?? -1,
            code: group.get('templateGroupGuid'),
            type: ComposerAttributeType.PreSetContainer,
            name: group.get('name') || T._('Pre-set configurations', 'Admin'),
            displayOrder: group.get('displayOrder') ?? 0,
            parentID: !guidIsEmpty(stepGuid) ? stepGuid : !guidIsEmpty(groupGuid) ? groupGuid : undefined,
            parentIDNumeric: !guidIsEmpty(stepGuid) ? group.get('stepID') : group.get('groupID'),
            parentType: !guidIsEmpty(stepGuid)
                ? ComposerAttributeType.AttributeStep
                : !guidIsEmpty(groupGuid)
                  ? ComposerAttributeType.AttributeGroup
                  : undefined,
            presetSelection: {
                compositions: [...(group.get('compositions') ?? [])],
                macrocategories: [...(group.get('macrocategories') ?? [])],
                categories: group.get('categories')?.map((category) => category.toJSON()) ?? []
            },
            data: group.toJSON()
        };
    }

    static fromPresetNode(node: ComposerAttributeNode): MPlaza.TemplateGroup {
        const group = ComposerAttributeNodeAdapter.hydrate(new MPlaza.TemplateGroup(), node.data);
        group.set('templateGroupGuid', node.id);
        group.set('templateGroupID', ComposerAttributeNodeAdapter.saveNumericID(node.idNumeric, node.id));
        group.set('name', node.name.trim());
        group.set('displayOrder', node.displayOrder);
        group.set('groupGuid', node.parentType === ComposerAttributeType.AttributeGroup ? node.parentID || Zakeke.getEmptyUUID() : Zakeke.getEmptyUUID());
        group.set('groupID', node.parentType === ComposerAttributeType.AttributeGroup ? node.parentIDNumeric : -1);
        group.set('stepGuid', node.parentType === ComposerAttributeType.AttributeStep ? node.parentID || Zakeke.getEmptyUUID() : Zakeke.getEmptyUUID());
        group.set('stepID', node.parentType === ComposerAttributeType.AttributeStep ? node.parentIDNumeric : -1);
        group.set('compositions', node.presetSelection?.compositions ?? []);
        group.set('macrocategories', node.presetSelection?.macrocategories ?? []);
        group.set('categories', node.presetSelection?.categories ?? []);
        return group;
    }

    static fromJSON(json: any): MPlaza.Attribute {
        const attribute = ComposerAttributeNodeAdapter.createNew();
        if (!json) {
            return attribute;
        }
        const parsedJSON = typeof json === 'string' ? JSON.parse(json) : json;
        if (parsedJSON) {
            attribute.set('attributeGuid', parsedJSON.attributeGuid || MPlaza.generateUUID());
            attribute.set('attributeCode', parsedJSON.attributeCode || randomCode('attribute'));
            attribute.set('attributeTypeID', parsedJSON.attributeTypeID || MPlaza.AttributeType.Options);
            attribute.set('stepGuid', parsedJSON.stepGuid || Zakeke.getEmptyUUID());
            attribute.set('groupGuid', parsedJSON.groupGuid || Zakeke.getEmptyUUID());
            attribute.set('stepID', parsedJSON.stepID || -1);
            attribute.set('groupID', parsedJSON.groupID || -1);
            attribute.set('name', parsedJSON.name || '');
            attribute.set('description', parsedJSON.description || '');
            attribute.set('enabled', parsedJSON.enabled !== false);
            attribute.set('displayOrder', parsedJSON.displayOrder || 0);
            attribute.set('defaultValue', parsedJSON.defaultValue || '');
            attribute.set('options', parsedJSON.options || []);
            attribute.set('preActions', parsedJSON.preActions || []);
            attribute.set('valueActions', parsedJSON.valueActions || []);
            attribute.set('postActions', parsedJSON.postActions || []);
            attribute.set('pricings', parsedJSON.pricings || []);
            attribute.set('constraintExpression', parsedJSON.constraintExpression || null);
            attribute.set('meshSelector', parsedJSON.meshSelector || null);
            attribute.set('sceneCameraLocationID', parsedJSON.sceneCameraLocationID || null);
            attribute.set('hideOptionsLabel', !!parsedJSON.hideOptionsLabel);
        }
        return attribute;
    }

    static fromJSONOption(json: any): MPlaza.AttributeOption {
        const option = ComposerAttributeNodeAdapter.createNewOption();
        if (!json) {
            return option;
        }
        const parsedJSON = typeof json === 'string' ? JSON.parse(json) : json;
        if (parsedJSON) {
            option.set('optionGuid', parsedJSON.optionGuid || MPlaza.generateUUID());
            option.set('name', parsedJSON.name || '');
            option.set('displayOrder', parsedJSON.displayOrder || 0);
            option.set('optionCode', parsedJSON.optionCode || randomCode('option'));
            option.set('description', parsedJSON.description || '');
            option.set('enabled', parsedJSON.enabled !== false);
            option.set('imageUrl', parsedJSON.imageUrl || '');
            option.set('tempImageFileKey', parsedJSON.tempImageFileKey || '');
            option.set('tempImageFileObjectID', parsedJSON.tempImageFileObjectID ?? -1);
            option.set('imageFileObjectID', parsedJSON.imageFileObjectID ?? -1);
            option.set('constraintExpression', parsedJSON.constraintExpression || null);
            option.set('isDefault', !!parsedJSON.isDefault);
            option.set('actions', parsedJSON.actions || []);
        }
        return option;
    }

    static fromJSONAction(json: any): MPlaza.AttributeSceneAction {
        const action = ComposerAttributeNodeAdapter.createNewAction();
        if (!json) {
            return action;
        }
        const parsedJSON = typeof json === 'string' ? JSON.parse(json) : json;
        if (parsedJSON) {
            action.set('actionGuid', parsedJSON.actionGuid || MPlaza.generateUUID());
            action.set('name', parsedJSON.name || '');
            action.set('enabled', parsedJSON.enabled !== false);
            action.set('selector', parsedJSON.selector || '');
            action.set('value', parsedJSON.value || '');
        }
        return action;
    }

    static fromJSONStep(json: any): MPlaza.AttributeStep {
        const step = ComposerAttributeNodeAdapter.createNewStep();
        if (!json) {
            return step;
        }
        const parsedJSON = typeof json === 'string' ? JSON.parse(json) : json;
        if (parsedJSON) {
            step.set('stepGuid', parsedJSON.stepGuid || MPlaza.generateUUID());
            step.set('name', parsedJSON.name || '');
            step.set('enabled', parsedJSON.enabled !== false);
            step.set('displayOrder', parsedJSON.displayOrder || 0);
            step.set('groupGuid', parsedJSON.groupGuid || Zakeke.getEmptyUUID());
        }
        return step;
    }

    static fromJSONGroup(json: any): MPlaza.AttributeGroup {
        const group = ComposerAttributeNodeAdapter.createNewGroup();
        if (!json) {
            return group;
        }
        const parsedJSON = typeof json === 'string' ? JSON.parse(json) : json;
        if (parsedJSON) {
            group.set('groupGuid', parsedJSON.groupGuid || MPlaza.generateUUID());
            group.set('name', parsedJSON.name || '');
            group.set('enabled', parsedJSON.enabled !== false);
            group.set('displayOrder', parsedJSON.displayOrder || 0);
        }
        return group;
    }

    static createExpression(link: ComposerAttributeNodeLink): MPlaza.AttributeProposition {
        const expression = new MPlaza.AttributeProposition();
        expression.set('attributeGuid', link.attributeID || '');
        expression.set('op', link.operation as unknown as MPlaza.ComparisonOperator);
        expression.set('value', link.optionID || '');
        return expression;
    }

    static createConstraint(links: ComposerAttributeNodeLink[], operation: ComposerLogicalOperator, source?: any): MPlaza.AttributeExpression {
        const expression = new MPlaza.AttributeExpression();
        const propositions = links.map((link) => {
            // AttributeExpression holds plain arrays, not Backbone relations. Leaving a model
            // here makes the product serializer read undefined fields and erase the condition.
            return ComposerAttributeNodeAdapter.createExpression(link).toJSON();
        });
        expression.set('propositions', propositions);
        expression.set('op', operation as unknown as MPlaza.LogicalOperator);
        expression.set('expressions', ComposerAttributeNodeAdapter.cloneData(source?.expressions) ?? []);
        return expression;
    }

    static fromNode(node: ComposerAttributeNode | null): MPlaza.Attribute {
        if (!node) {
            return ComposerAttributeNodeAdapter.createNew();
        }
        // Start from the complete model loaded from the API, then apply the fields edited by
        // this screen. Rebuilding a blank entity here used to silently discard fields that the
        // current editor does not expose (attribute actions/type/default and future API fields).
        const attribute = ComposerAttributeNodeAdapter.hydrate(new MPlaza.Attribute(), node.data);
        attribute.set('attributeGuid', node.id || MPlaza.generateUUID());
        attribute.set('attributeID', ComposerAttributeNodeAdapter.saveNumericID(node.idNumeric, node.id));
        attribute.set('attributeCode', node.code || randomCode('attribute'));
        attribute.set('attributeTypeID', node.attributeType);
        attribute.set('name', node.name || '');
        attribute.set('description', node.description || '');
        attribute.set('enabled', node.enabled !== false);
        attribute.set('displayOrder', node.displayOrder || 0);
        attribute.set('stepGuid', node.parentType === ComposerAttributeType.AttributeStep ? node.parentID : Zakeke.getEmptyUUID());
        attribute.set('stepID', node.parentType === ComposerAttributeType.AttributeStep ? node.parentIDNumeric : -1);
        attribute.set('groupGuid', node.parentType === ComposerAttributeType.AttributeGroup ? node.parentID : Zakeke.getEmptyUUID());
        attribute.set('groupID', node.parentType === ComposerAttributeType.AttributeGroup ? node.parentIDNumeric : -1);
        const options = new MPlaza.AttributeOptions();
        node.options.forEach((option) => {
            const hasPricing = node.pricings.some((pricing) => pricing.selectedOptionGuid === option.id);
            options.push(ComposerAttributeNodeAdapter.fromOptionNode(option, hasPricing));
        });
        attribute.set('options', options);
        const originalPricings = attribute.get('pricings');
        const pricings = new MPlaza.AttributePricings();
        node.pricings.forEach((nodePricing, pricingIndex) => {
            const originalPricing = originalPricings?.find?.(
                (item: any) =>
                    (nodePricing.attributePricingID > 0 && (item.get?.('attributePricingID') ?? item.attributePricingID) === nodePricing.attributePricingID) ||
                    (!!nodePricing.selectedOptionGuid && (item.get?.('selectedOptionGuid') ?? item.selectedOptionGuid) === nodePricing.selectedOptionGuid)
            );
            const pricing = ComposerAttributeNodeAdapter.hydrate(new MPlaza.AttributePricing(), originalPricing?.toJSON?.() ?? originalPricing);
            pricing.set(
                'attributePricingID',
                ComposerAttributeNodeAdapter.saveNumericID(
                    nodePricing.attributePricingID,
                    `${node.id}:${nodePricing.selectedOptionGuid || nodePricing.selectedValue}:${pricingIndex}`
                )
            );
            pricing.set('selectedOptionID', nodePricing.selectedOptionID || null);
            pricing.set('selectedOptionGuid', nodePricing.selectedOptionGuid || null);
            pricing.set('selectedValue', nodePricing.selectedValue || null);
            pricing.set('price', nodePricing.price || 0);
            pricing.set('pricingID', nodePricing.pricingID || 0);
            if (nodePricing.name) (pricing as any).set('name', nodePricing.name);
            pricings.push(pricing);
        });
        attribute.set('pricings', pricings);
        attribute.set('optionPreviewShapeID', (node.previewType as unknown as MPlaza.AttributeOptionPreviewShape) || 1);
        (attribute as any).set(
            'constraintExpression',
            node.links.length > 0 || node.data?.constraintExpression?.expressions?.length
                ? ComposerAttributeNodeAdapter.createConstraint(node.links, node.linkOperation ?? ComposerLogicalOperator.AND, node.data?.constraintExpression)
                : null
        );
        attribute.set('meshSelector', node.objects || null);
        attribute.set('sceneCameraLocationID', node.camera || null);
        attribute.set('hideOptionsLabel', node.hideOptionsLabel || false);
        return attribute;
    }

    static fromGroupNode(node: ComposerAttributeNode | null): MPlaza.AttributeGroup {
        if (!node) {
            return ComposerAttributeNodeAdapter.createNewGroup();
        }
        const group = ComposerAttributeNodeAdapter.hydrate(new MPlaza.AttributeGroup(), node.data);
        group.set('groupGuid', node.id || MPlaza.generateUUID());
        group.set('groupID', ComposerAttributeNodeAdapter.saveNumericID(node.idNumeric, node.id));
        group.set('name', node.name || '');
        group.set('enabled', node.enabled !== false);
        group.set('displayOrder', node.displayOrder || 0);
        group.set('imageUrl', node.img || '');
        // Preserve the temporary upload reference so the API can persist a newly
        // uploaded group image instead of treating it as a URL-only change.
        group.set('tempImageFileKey', node.img ? node.data?.tempImageFileKey || '' : '');
        group.set('sceneCameraLocationID', node.camera || null);
        group.set('meshSelector', node.objects || null);
        group.set('direction', node.direction ?? 0);
        group.set('attributesAlwaysOpened', node.attributesAlwaysOpened ?? false);
        return group;
    }

    static fromStepNode(node: ComposerAttributeNode | null): MPlaza.AttributeStep {
        if (!node) {
            return ComposerAttributeNodeAdapter.createNewStep();
        }
        const step = ComposerAttributeNodeAdapter.hydrate(new MPlaza.AttributeStep(), node.data);
        step.set('stepGuid', node.id || MPlaza.generateUUID());
        step.set('stepID', ComposerAttributeNodeAdapter.saveNumericID(node.idNumeric, node.id));
        step.set('name', node.name || '');
        step.set('enabled', node.enabled !== false);
        step.set('displayOrder', node.displayOrder || 0);
        step.set('groupGuid', node.parentID || Zakeke.getEmptyUUID());
        step.set('groupID', node.parentIDNumeric || -1);
        (step as any).set('cameraLocationID', node.camera || null);
        return step;
    }

    static fromOptionNode(node: ComposerAttributeOptionNode | null, preserveEmptyCode = false): MPlaza.AttributeOption {
        if (!node) {
            return ComposerAttributeNodeAdapter.createNewOption();
        }
        const option = ComposerAttributeNodeAdapter.hydrate(new MPlaza.AttributeOption(), node.data);
        option.set('optionGuid', node.id || MPlaza.generateUUID());
        option.set('optionID', ComposerAttributeNodeAdapter.saveNumericID(node.idNumeric, node.id));
        option.set('name', node.name || '');
        option.set('description', node.description || '');
        option.set('enabled', node.enabled !== false);
        option.set('displayOrder', node.displayOrder || 0);
        option.set('optionCode', preserveEmptyCode ? node.code : node.code || randomCode('option'));
        option.set('imageUrl', node.img || '');
        // Rebuilding an AttributeOption without these file references makes the API
        // interpret a normal save as an explicit image deletion. Keep the references
        // captured when the option was loaded, unless the image was actually removed.
        option.set('tempImageFileKey', node.img ? node.data?.tempImageFileKey || '' : '');
        option.set('tempImageFileObjectID', node.img ? (node.data?.tempImageFileObjectID ?? -1) : -1);
        option.set('imageFileObjectID', node.img ? (node.data?.imageFileObjectID ?? -1) : -1);
        option.set(
            'constraintExpression',
            node.links.length > 0 || node.data?.constraintExpression?.expressions?.length
                ? ComposerAttributeNodeAdapter.createConstraint(node.links, node.linkOperation ?? ComposerLogicalOperator.AND, node.data?.constraintExpression)
                : null
        );
        option.set('isDefault', node.isDefault || false);
        const actions = new MPlaza.AttributeSceneActions();
        node.actions.forEach((action) => {
            actions.push(ComposerAttributeNodeAdapter.fromActionNode(action));
        });
        option.set('actions', actions);
        (option as any).set(
            'extensionFields',
            (node.extensionFields ?? []).map((ef) => ({
                extensionFieldID: ef.extensionFieldID,
                name: ef.name || '',
                label: ef.label || '',
                value: ef.value || '',
                dataTypeID: ef.dataTypeID || 0,
                operationTypeID: ef.operationTypeID || 0,
                formatString: ef.formatString || ''
            }))
        );
        return option;
    }

    static fromActionNode(node: ComposerOptionActionNode | null): MPlaza.AttributeSceneAction {
        if (!node) {
            return ComposerAttributeNodeAdapter.createNewAction();
        }
        const action = ComposerAttributeNodeAdapter.hydrate(new MPlaza.AttributeSceneAction(), node.data);
        action.set('actionGuid', node.id || MPlaza.generateUUID());
        (action as any).set('actionID', ComposerAttributeNodeAdapter.saveNumericID(node.data?.actionGuid === node.id ? node.data?.actionID : null, node.id));
        action.set('name', node.name || '');
        action.set('enabled', node.enabled !== false);
        action.set('actionTypeID', GetActionValueFromType(node?.type || ComposerOptionActionsType.SHOW_OBJECTS));
        action.set('sceneElementTypeID', node.sceneElementType ?? MPlaza.SceneElementType.Mesh);
        action.set('execOrder', node.displayOrder || 0);
        action.set('selector', node.selector || '');
        action.set('propertyName', node.property || '');
        action.set('value', node.value || '');
        return action;
    }

    static createNew(displayOrder?: number): MPlaza.Attribute {
        const attribute = new MPlaza.Attribute();
        attribute.set('attributeID', -1);
        attribute.set('attributeGuid', MPlaza.generateUUID());
        attribute.set('attributeCode', randomCode('attribute'));
        // attribute.set('attributeTypeID', MPlaza.AttributeType.Options);
        attribute.set('stepGuid', Zakeke.getEmptyUUID());
        attribute.set('groupGuid', Zakeke.getEmptyUUID());
        attribute.set('stepID', -1);
        attribute.set('groupID', -1);
        // attribute.set('constraintExpression', null);
        attribute.set('name', '');
        attribute.set('description', '');
        attribute.set('enabled', true);
        attribute.set('displayOrder', displayOrder || 0);

        return attribute;
    }
    static createNewGroup(displayOrder?: number): MPlaza.AttributeGroup {
        const group = new MPlaza.AttributeGroup();
        group.set('groupGuid', MPlaza.generateUUID());
        group.set('groupID', -1);
        group.set('name', '');
        group.set('enabled', true);
        group.set('displayOrder', displayOrder || 0);
        return group;
    }

    static createNewStep(displayOrder?: number): MPlaza.AttributeStep {
        const step = new MPlaza.AttributeStep();
        step.set('stepGuid', MPlaza.generateUUID());
        step.set('stepID', -1);
        step.set('name', '');
        step.set('enabled', true);
        step.set('displayOrder', displayOrder || 0);
        return step;
    }

    static createNewOption(displayOrder?: number): MPlaza.AttributeOption {
        const option = new MPlaza.AttributeOption();
        option.set('optionGuid', MPlaza.generateUUID());
        option.set('optionID', -1);
        option.set('name', '');
        option.set('enabled', true);
        option.set('optionCode', randomCode('option'));
        option.set('displayOrder', displayOrder || 0);
        return option;
    }

    static createNewAction(): MPlaza.AttributeSceneAction {
        const action = new MPlaza.AttributeSceneAction();
        action.set('actionGuid', MPlaza.generateUUID());
        action.set('name', '');
        action.set('enabled', true);
        action.set('execOrder', 0);
        action.set('actionTypeID', GetActionValueFromType(ComposerOptionActionsType.SHOW_OBJECTS));
        action.set('sceneElementTypeID', MPlaza.SceneElementType.Mesh);
        action.set('selector', '');
        return action;
    }

    static toNode(attribute: MPlaza.Attribute): ComposerAttributeNode {
        if (!attribute) {
            return ComposerAttributeNodeAdapter.toNode(ComposerAttributeNodeAdapter.createNew());
        }
        const attributeCode = attribute.get('attributeCode') || randomCode('attribute');
        const pricedOptionGuids = new Set<string>(
            attribute.get('pricings')?.map((pricing: any) => pricing.get?.('selectedOptionGuid') ?? pricing.selectedOptionGuid ?? '') || []
        );
        return {
            id: attribute.get('attributeGuid'),
            idNumeric: attribute.get('attributeID') || -1,
            attributeType: attribute.get('attributeTypeID') || MPlaza.AttributeType.Options,
            code: attributeCode,
            description: attribute.get('description') || '',
            enabled: attribute.get('enabled') !== false,
            name: attribute.get('name') || T._('Attribute', 'Admin'),
            displayOrder: attribute.get('displayOrder') || 0,
            type: ComposerAttributeType.Attribute,
            objects: attribute.get('meshSelector') || '',
            camera: attribute.get('sceneCameraLocationID') || '',
            parentID: getParentGuid(attribute.get('stepGuid'), attribute.get('groupGuid')),
            parentIDNumeric: getParentIDNumeric(attribute.get('stepID'), attribute.get('groupID')),
            parentType: getParentType(attribute.get('stepGuid'), attribute.get('groupGuid')),
            options:
                attribute
                    .get('options')
                    ?.map((option) =>
                        ComposerAttributeNodeAdapter.toNodeOption(attribute.get('attributeGuid'), option, pricedOptionGuids.has(option.get('optionGuid')))
                    ) || [],
            pricings:
                attribute.get('pricings')?.map((p: any) => ({
                    attributePricingID: p.get?.('attributePricingID') ?? p.attributePricingID ?? 0,
                    selectedOptionID: p.get?.('selectedOptionID') ?? p.selectedOptionID ?? 0,
                    selectedOptionGuid: p.get?.('selectedOptionGuid') ?? p.selectedOptionGuid ?? '',
                    selectedValue: p.get?.('selectedValue') ?? p.selectedValue ?? '',
                    price: p.get?.('price') ?? p.price ?? 0,
                    pricingID: p.get?.('pricingID') ?? p.pricingID ?? 0,
                    name: p.get?.('name') ?? p.name ?? ''
                })) || [],
            platformAttribute: attributeCode.startsWith('{') ? tryParsePlatformValue(attributeCode) : undefined,
            previewType: (attribute.get('optionPreviewShapeID') as unknown as ComposerAttributePreviewType) || ComposerAttributePreviewType.Rectangle,
            hideOptionsLabel: attribute.get('hideOptionsLabel') || false,
            linkOperation: (attribute.get('constraintExpression')?.get('op') as unknown as ComposerLogicalOperator) || ComposerLogicalOperator.AND,
            links:
                attribute
                    .get('constraintExpression')
                    ?.get('propositions')
                    ?.map((link: any) => ({
                        attributeID: link.attributeGuid || '',
                        optionID: link.value || '',
                        operation: link.op || ComposerComparisonOperator.EQUAL
                    })) || [],
            brokenValues: new Map(),
            data: attribute.toJSON()
        };
    }

    static toNodeActionFromAiAttribute(attributeID: string, optionID: string, action: AttributePreviewAction): ComposerOptionActionNode {
        const nodeAction = ComposerAttributeNodeAdapter.toNodeAction(attributeID, optionID, ComposerAttributeNodeAdapter.createNewAction());
        nodeAction.type = GetActionTypeFromAiType(action.type);
        nodeAction.selector = action.objects.join(';');
        nodeAction.value = action.material;
        return nodeAction;
    }

    static toNodeOptionFromAiAttribute(attributeID: string, option: AttributePreviewOption): ComposerAttributeOptionNode {
        const nodeOption = ComposerAttributeNodeAdapter.toNodeOption(attributeID, ComposerAttributeNodeAdapter.createNewOption());
        nodeOption.name = option.name;
        nodeOption.actions = option.actions.map((action) => ComposerAttributeNodeAdapter.toNodeActionFromAiAttribute(attributeID, nodeOption.id, action));
        return nodeOption;
    }

    static toNodeFromAiAttribute(attribute: AttributePreviewItems): ComposerAttributeNode {
        const nodeAttribute = ComposerAttributeNodeAdapter.toNode(ComposerAttributeNodeAdapter.createNew());
        nodeAttribute.name = attribute.name;
        nodeAttribute.options = attribute.options.map((option) => ComposerAttributeNodeAdapter.toNodeOptionFromAiAttribute(nodeAttribute.id, option));
        return nodeAttribute;
    }

    static toNodeOption(attributeID: string, option: MPlaza.AttributeOption, preserveEmptyCode = false): ComposerAttributeOptionNode {
        if (!option) {
            return ComposerAttributeNodeAdapter.toNodeOption(attributeID, ComposerAttributeNodeAdapter.createNewOption());
        }
        return {
            id: option.get('optionGuid'),
            idNumeric: option.get('optionID') || -1,
            code: preserveEmptyCode ? option.get('optionCode') || '' : option.get('optionCode') || randomCode('option'),
            name: option.get('name') || T._('Option', 'Admin'),
            description: option.get('description') || '',
            enabled: option.get('enabled') !== false,
            displayOrder: option.get('displayOrder') || 0,
            type: ComposerAttributeType.Attribute,
            img: option.get('imageUrl') || '',
            attributeID: attributeID,
            isDefault: option.get('isDefault') || false,
            platformAttribute: tryParsePlatformValue(option.get('optionCode')),
            linkOperation: (option.get('constraintExpression')?.get('op') as unknown as ComposerLogicalOperator) || ComposerLogicalOperator.AND,
            links:
                option
                    .get('constraintExpression')
                    ?.get('propositions')
                    ?.map((link: any) => ({
                        attributeID: link.attributeGuid || '',
                        optionID: link.value || '',
                        operation: link.op || ComposerComparisonOperator.EQUAL
                    })) || [],
            actions: option.get('actions')?.map((action) => ComposerAttributeNodeAdapter.toNodeAction(attributeID, option.get('optionGuid'), action)) || [],
            extensionFields:
                (option as any).get('extensionFields')?.map((ef: any) => ({
                    extensionFieldID: ef.get?.('extensionFieldID') ?? ef.extensionFieldID ?? 0,
                    name: ef.get?.('name') ?? ef.name ?? '',
                    label: ef.get?.('label') ?? ef.label ?? '',
                    value: ef.get?.('value') ?? ef.value ?? '',
                    dataTypeID: ef.get?.('dataTypeID') ?? ef.dataTypeID ?? 0,
                    operationTypeID: ef.get?.('operationTypeID') ?? ef.operationTypeID ?? 0,
                    formatString: ef.get?.('formatString') ?? ef.formatString ?? ''
                })) || [],
            brokenValues: new Map(),
            data: option.toJSON()
        } as ComposerAttributeOptionNode;
    }

    static toNodeAction(attributeID: string, optionID: string, action: MPlaza.AttributeSceneAction): ComposerOptionActionNode {
        if (!action) {
            return ComposerAttributeNodeAdapter.toNodeAction(attributeID, optionID, ComposerAttributeNodeAdapter.createNewAction());
        }
        return {
            id: action.get('actionGuid'),
            name: action.get('name') || T._('Action', 'Admin'),
            enabled: action.get('enabled') !== false,
            type: GetActionTypeFromValue(action.get('actionTypeID'), action.get('value') ?? ''),
            sceneElementType: action.get('sceneElementTypeID') ?? MPlaza.SceneElementType.Mesh,
            displayOrder: action.get('execOrder') || 0,
            optionID: optionID,
            attributeID: attributeID,
            selector: action.get('selector') || '',
            property: action.get('propertyName') || '',
            value: action.get('value') || '',
            brokenValues: new Map(),
            data: action.toJSON()
        } as ComposerOptionActionNode;
    }

    static toStepNode(step: MPlaza.AttributeStep): ComposerAttributeNode {
        if (!step) {
            return ComposerAttributeNodeAdapter.toNode(ComposerAttributeNodeAdapter.createNew());
        }
        const groupGuid = step.get('groupGuid');
        return {
            id: step.get('stepGuid'),
            idNumeric: step.get('stepID') || -1,
            attributeType: MPlaza.AttributeType.Options,
            code: step.get('stepGuid') || '',
            description: '',
            name: step.get('name') || T._('Step', 'Admin'),
            enabled: step.get('enabled') !== false,
            displayOrder: step.get('displayOrder') || 0,
            type: ComposerAttributeType.AttributeStep,
            parentID: guidIsEmpty(groupGuid) ? undefined : groupGuid || undefined,
            parentIDNumeric: step.get('groupID') || -1,
            parentType: guidIsEmpty(groupGuid) ? undefined : ComposerAttributeType.AttributeGroup,
            camera: (step as any).get('cameraLocationID') || undefined,
            options: [],
            pricings: [],
            links: [],
            brokenValues: new Map(),
            data: step.toJSON()
        };
    }

    static toGroupNode(group: MPlaza.AttributeGroup): ComposerAttributeNode {
        if (!group) {
            return ComposerAttributeNodeAdapter.toNode(ComposerAttributeNodeAdapter.createNew());
        }
        return {
            id: group.get('groupGuid'),
            idNumeric: group.get('groupID') || -1,
            attributeType: MPlaza.AttributeType.Options,
            code: group.get('groupGuid') || '',
            name: group.get('name') || T._('Group', 'Admin'),
            enabled: group.get('enabled') !== false,
            description: '',
            displayOrder: group.get('displayOrder') || 0,
            type: ComposerAttributeType.AttributeGroup,
            img: group.get('imageUrl') || '',
            parentID: undefined,
            parentIDNumeric: -1,
            camera: group.get('sceneCameraLocationID') || undefined,
            objects: group.get('meshSelector') || undefined,
            direction: group.get('direction') ?? 0,
            attributesAlwaysOpened: group.get('attributesAlwaysOpened') || false,
            options: [],
            pricings: [],
            links: [],
            brokenValues: new Map(),
            data: group.toJSON()
        };
    }

    static adjustValue(attribute: ComposerAttributeNode): ComposerAttributeNode {
        const adjustedAttribute = { ...attribute };
        if (adjustedAttribute.options && adjustedAttribute.options.every((o) => !o.isDefault)) {
            adjustedAttribute.options = adjustedAttribute.options.map((option, index) => {
                return {
                    ...option,
                    isDefault: index === 0 // Set the first option as default
                };
            });
        }
        return adjustedAttribute;
    }

    static adjustValues(attributes: ComposerAttributeNode[]): ComposerAttributeNode[] {
        return attributes.map(ComposerAttributeNodeAdapter.adjustValue);
    }
}

export class ComposerTemplateNodeAdapter {
    private static setAreaGeometry(area: MPlaza.Area, node: ComposerAreaNode | null, ppcm: number) {
        const width = Math.max(0.01, node?.width || 10) * ppcm;
        const height = Math.max(0.01, node?.height || 10) * ppcm;
        const path = area.get('path') || '';
        const sizeChanged = Math.abs((area.get('boundsWidth') || 0) - width) > 0.01 || Math.abs((area.get('boundsHeight') || 0) - height) > 0.01;
        const shouldReplacePath = !path || path.includes('00470596-d7ac-4b82-ebe0-d3b207794bb1') || sizeChanged;
        area.set('boundsWidth', width);
        area.set('boundsHeight', height);
        if (shouldReplacePath) {
            area.set('path', JSON.stringify(getAreaPath(width, height)));
            area.set('pathSVG', getAreaSVG(width, height));
            area.set('boundsX', -width / 2);
            area.set('boundsY', -height / 2);
        }
    }

    static toNode(id: number, name: string, design: any, nodeID: string | null = null, model: MPlaza.Model, sides: MPlaza.Sides): ComposerTemplateNode {
        const designObj: MPlaza.Design = design
            ? new MPlaza.Design(JSON.parse(JSON.stringify(design)))
            : ComposerTemplateNodeAdapter.createNewDesign(name, model, sides);
        restoreComposerPlaceholderAreas(designObj, model);
        const designSides = designObj.get('sides');
        const restrictions = new Map();
        for (const restriction of designSides.models) {
            const uploadRestrictions = restriction?.get('uploadRestrictions');
            const objRestriction: ComposerTemplateSideRestrictions = {
                userCanAddTexts: restriction.get('canAddText') ?? true,
                userCanAddImages: restriction.get('canAddImage') ?? true,
                isGalleryEnabled: !restriction.get('disableSellerImages'),
                userCanAddUploadImages: uploadRestrictions?.isUserImageAllowed ?? undefined,
                jpgUploadEnabled: uploadRestrictions?.isJpgAllowed ?? undefined,
                pngUploadEnabled: uploadRestrictions?.isPngAllowed ?? undefined,
                svgUploadEnabled: uploadRestrictions?.isSvgAllowed ?? undefined,
                pdfUploadEnabled: uploadRestrictions?.isPdfAllowed ?? undefined,
                pdfWithRasterizationEnabled: uploadRestrictions?.isPdfWithRasterAllowed ?? undefined,
                epsUploadEnabled: uploadRestrictions?.isEpsAllowed ?? undefined
            };
            restrictions.set(restriction.get('sideID'), objRestriction);
        }
        return {
            templateID: id,
            designDocID: design?.docID || '',
            name: name,
            design: designObj,

            //restriction
            restrictions: restrictions,
            //ui
            id: nodeID ?? MPlaza.generateUUID(),
            data: null, //items?.toJSON(),
            expanded: true
        };
    }

    static fromTemplateNode(node: ComposerTemplateNode): MPlaza.Design {
        const design = new MPlaza.Design(JSON.parse(JSON.stringify(node.design)));
        design.tempID = node.design.tempID;
        const designSides = design.get('sides');
        for (const designSide of designSides.models) {
            const sideRestrictions = node.restrictions.get(designSide.get('sideID'));
            if (!sideRestrictions) continue;
            if (sideRestrictions.userCanAddTexts !== undefined) designSide.set('canAddText', sideRestrictions.userCanAddTexts);
            if (sideRestrictions.userCanAddImages !== undefined) designSide.set('canAddImage', sideRestrictions.userCanAddImages);
            if (sideRestrictions.isGalleryEnabled !== undefined) designSide.set('disableSellerImages', !sideRestrictions.isGalleryEnabled);
            const uploadRestrictions = designSide?.get('uploadRestrictions');

            if (uploadRestrictions) {
                if (sideRestrictions.userCanAddUploadImages !== undefined) uploadRestrictions.isUserImageAllowed = sideRestrictions.userCanAddUploadImages;
                if (sideRestrictions.jpgUploadEnabled !== undefined) uploadRestrictions.isJpgAllowed = sideRestrictions.jpgUploadEnabled;
                if (sideRestrictions.pngUploadEnabled !== undefined) uploadRestrictions.isPngAllowed = sideRestrictions.pngUploadEnabled;
                if (sideRestrictions.svgUploadEnabled !== undefined) uploadRestrictions.isSvgAllowed = sideRestrictions.svgUploadEnabled;
                if (sideRestrictions.pdfUploadEnabled !== undefined) uploadRestrictions.isPdfAllowed = sideRestrictions.pdfUploadEnabled;
                if (sideRestrictions.pdfWithRasterizationEnabled !== undefined)
                    uploadRestrictions.isPdfWithRasterAllowed = sideRestrictions.pdfWithRasterizationEnabled;
                if (sideRestrictions.epsUploadEnabled !== undefined) uploadRestrictions.isEpsAllowed = sideRestrictions.epsUploadEnabled;
            }
        }
        return design;
    }

    static withAreaSide(template: ComposerTemplateNode, area: ComposerAreaNode, model: MPlaza.Model): ComposerTemplateNode {
        const design = new MPlaza.Design(JSON.parse(JSON.stringify(template.design)));
        design.tempID = template.design.tempID;
        const sides = design.get('sides');
        if (sides.models.some((side) => side.get('sideID') === area.sideID)) return template;

        sides.add(
            new (MPlaza.DesignSide as any)({
                modelID: model.get('modelID'),
                colorID: model.get('colors').at(0).get('colorID'),
                sideID: area.sideID,
                canAddText: true,
                canAddImage: true,
                disableSellerImages: false,
                uploadRestrictions: { isUserImageAllowed: true, isJpgAllowed: true, isPngAllowed: true, isSvgAllowed: true }
            })
        );
        const restrictions = new Map(template.restrictions);
        restrictions.set(area.sideID, { userCanAddTexts: true, userCanAddImages: true, isGalleryEnabled: true, userCanAddUploadImages: true });
        return { ...template, design, restrictions };
    }

    static createNewDesign(name: string, model: MPlaza.Model, sides: MPlaza.Sides | MPlaza.Side): MPlaza.Design {
        const design = new MPlaza.Design();
        design.set('name', name);
        design.setModel(model);
        // Configurator products no longer get an auto-created default printing method, so printTypes may be empty.
        // The active PM is selected per attribute option via the SetPrintingMethod scene action instead.
        const defaultPrintType = model.get('printTypes')?.at(0);
        if (defaultPrintType) design.setPrintType(defaultPrintType);
        design.set('sides', new MPlaza.DesignSides());
        if (sides instanceof MPlaza.Side) {
            const designSide = new (MPlaza.DesignSide as any)({
                modelID: model.get('modelID'),
                colorID: model.get('colors').at(0).get('colorID'),
                sideID: sides.get('sideID'),
                canAddText: true,
                canAddImage: true,
                disableSellerImages: false,
                maxNrTexts: null,
                maxNrImages: null,
                uploadRestrictions: {
                    isSvgAllowed: true,
                    isPngAllowed: true,
                    isJpgAllowed: true
                }
            });
            design.get('sides').add(designSide);
        } else {
            for (const side of sides.models) {
                const designSide = new (MPlaza.DesignSide as any)({
                    modelID: model.get('modelID'),
                    colorID: model.get('colors').at(0).get('colorID'),
                    sideID: side.get('sideID'),
                    canAddText: true,
                    canAddImage: true,
                    disableSellerImages: false,
                    maxNrTexts: null,
                    maxNrImages: null,
                    uploadRestrictions: {
                        isSvgAllowed: true,
                        isPngAllowed: true,
                        isJpgAllowed: true
                    }
                });
                design.get('sides').add(designSide);
            }
        }
        design.tempID = Zakeke.generateUUID();
        return design;
    }

    static createNewTemplate(name: string, model: MPlaza.Model, side: MPlaza.Side): ComposerTemplateNode {
        return {
            templateID: 0,
            designDocID: '',
            name: name,
            design: ComposerTemplateNodeAdapter.createNewDesign(name, model, side),

            //restriction
            restrictions: new Map(),
            //ui
            id: MPlaza.generateUUID(),
            data: null, //items?.toJSON(),
            expanded: true
        };
    }

    static toNodeArea(side: MPlaza.Side, meshSides: MPlaza.ModelSceneMeshSides): ComposerAreaNode {
        const seenMeshSides = new Set<string>();
        const assignedMeshSides = meshSides.filter((mesh) => {
            if (mesh.get('sideID') !== side.get('sideID')) return false;
            const key = `${mesh.get('meshID')}:${mesh.get('subMeshID') ?? 0}`;
            if (seenMeshSides.has(key)) return false;
            seenMeshSides.add(key);
            return true;
        });
        const meshSide = assignedMeshSides.at(0);
        const printArea = side.get('areas').at(0);
        const ppcm = side.get('ppcm') || 1;
        const hasLegacyCentimeterBounds = !printArea.get('path') && ppcm >= 10 && printArea.get('boundsWidth') <= 100;
        const desktopTextureResolution = meshSide?.get('config')?.get('general')?.get('desktopTextureResolutionWidth') || undefined;
        const mobileTextureResolution = meshSide?.get('config')?.get('general')?.get('mobileTextureResolutionWidth') || undefined;
        return {
            name: side.get('name') || '',
            sideID: side.get('sideID') || 0,
            attributeID: meshSide?.get('attributeID') ?? undefined,
            displayOrder: side.get('sortOrder') ?? 0,
            ppcm,
            width: hasLegacyCentimeterBounds ? printArea.get('boundsWidth') : (printArea.get('boundsWidth') || 0) / ppcm,
            height: hasLegacyCentimeterBounds ? printArea.get('boundsHeight') : (printArea.get('boundsHeight') || 0) / ppcm,
            //effect: presente commentato nel codice ma forse deprecato?
            camera: (side as any).get('cameraLocationID') || undefined,
            uvMap: meshSide?.get('uvChannel') || 0,
            customTextureResolution: !!desktopTextureResolution || !!mobileTextureResolution,
            desktopTextureResolution: desktopTextureResolution,
            mobileTextureResolution: mobileTextureResolution,
            originalSide: side ?? new MPlaza.Side(),
            originalMeshSides: assignedMeshSides.map((item) => item),
            objects: Array.from(new Set(assignedMeshSides.map((mesh) => mesh.get('meshID')))).join(';'),

            //ui
            id: (side as any).get('sideGuid') || (side as any).get('id') || MPlaza.generateUUID(),
            data: {
                side: side.toJSON(),
                meshSides: assignedMeshSides.map((item) => item.toJSON())
            },
            templateID: '',
            brokenValues: new Map()
        };
    }

    static fromAreaNode(node: ComposerAreaNode | null): { side: MPlaza.Side; modelSides: MPlaza.ModelSceneMeshSides } {
        const result: { side: MPlaza.Side; modelSides: MPlaza.ModelSceneMeshSides } = {
            side: node?.originalSide || new MPlaza.Side(),
            modelSides: new MPlaza.ModelSceneMeshSides()
        };
        const sideGuid = (result.side as any).get('sideGuid') || node?.id || MPlaza.generateUUID();
        const sideID = getComposerAreaSideID(node?.sideID, result.side.get('sideID'), ComposerAttributeNodeAdapter.temporaryNumericID(sideGuid));
        (result.side as any).set('id', sideGuid);
        result.side.set('sideID', sideID);
        (result.side as any).set('sideGuid', sideGuid);
        // A new printable side must have a name: ProductService creates a required-name
        // Side record before it can persist the area's mesh mapping. Legacy sides may
        // legitimately arrive with an empty projection, so only fill missing new names.
        const sideName = node?.name?.trim() || result.side.get('name')?.trim() || (node && sideID <= 0 ? `Area ${(node.displayOrder ?? 0) + 1}` : '');
        result.side.set('name', sideName);
        result.side.set('sortOrder', node?.displayOrder ?? 0);
        const ppcm = node?.ppcm || 1024 / (node?.width || 10);
        result.side.set('ppcm', ppcm);
        if (result.side.get('areas').length > 0) {
            const area = result.side.get('areas').at(0);
            area.set('guid', area.get('guid') || MPlaza.generateUUID());
            ComposerTemplateNodeAdapter.setAreaGeometry(area, node, ppcm);
        } else {
            const areas = new MPlaza.Areas();
            const area = new MPlaza.Area();
            // -1 requests a server-assigned ID. Zero forces a literal RequiredAreaID=0.
            area.set('areaID', -1);
            area.set('guid', MPlaza.generateUUID());
            area.set('name', node?.name?.trim() || sideName);
            ComposerTemplateNodeAdapter.setAreaGeometry(area, node, ppcm);
            areas.add(area);
            result.side.set('areas', areas);
        }
        const selectedMeshIDs = new Set((node?.objects.split(';') || []).filter(Boolean));
        const persistedMeshSides = node?.originalMeshSides ?? [];
        for (const meshID of selectedMeshIDs) {
            const seenSubMeshIDs = new Set<number>();
            const matchingMeshSides = persistedMeshSides.filter((item) => {
                if (item.get('meshID') !== meshID) return false;
                const subMeshID = item.get('subMeshID') ?? 0;
                if (seenSubMeshIDs.has(subMeshID)) return false;
                seenSubMeshIDs.add(subMeshID);
                return true;
            });
            const sourceMeshSides: Array<MPlaza.ModelSceneMeshSide | null> = matchingMeshSides.length > 0 ? matchingMeshSides : [null];
            for (const sourceMeshSide of sourceMeshSides) {
                const mesh = sourceMeshSide ?? new MPlaza.ModelSceneMeshSide();
                mesh.set('meshID', meshID);
                mesh.set('sideID', result.side.get('sideID'));
                mesh.set('key', `${meshID}_${mesh.get('subMeshID') ?? 0}_${result.side.get('sideID')}`);
                mesh.set('attributeID', node?.attributeID && node.attributeID > 0 ? node.attributeID : null);
                mesh.set('uvChannel', node?.uvMap ?? mesh.get('uvChannel') ?? 0);
                const generalConfig = mesh.getOrCreateConfig().getGeneralConfig();
                (generalConfig as any)?.set(
                    'desktopTextureResolutionWidth',
                    node?.desktopTextureResolution ?? (generalConfig as any).get('desktopTextureResolutionWidth')
                );
                (generalConfig as any)?.set(
                    'desktopTextureResolutionHeight',
                    node?.desktopTextureResolution ?? (generalConfig as any).get('desktopTextureResolutionHeight')
                );
                (generalConfig as any)?.set(
                    'mobileTextureResolutionWidth',
                    node?.mobileTextureResolution ?? (generalConfig as any).get('mobileTextureResolutionWidth')
                );
                (generalConfig as any)?.set(
                    'mobileTextureResolutionHeight',
                    node?.mobileTextureResolution ?? (generalConfig as any).get('mobileTextureResolutionHeight')
                );
                mesh.getConfig()?.set('general', generalConfig);
                result.modelSides.add(mesh);
            }
        }
        return result;
    }

    static fromAreaNodeToSide(node: ComposerAreaNode | null): MPlaza.Side {
        const side = node?.originalSide || new MPlaza.Side();
        (side as any).set('id', node?.id || MPlaza.generateUUID());
        side.set('sideID', getComposerAreaSideID(node?.sideID, side.get('sideID'), node?.sideID || 0));
        side.set('name', node?.name || '');
        const ppcm = node?.ppcm || 1024 / (node?.width || 10);
        side.set('ppcm', ppcm);
        if (side.get('areas').length > 0) {
            const area = side.get('areas').at(0);
            area.set('guid', area.get('guid') || MPlaza.generateUUID());
            ComposerTemplateNodeAdapter.setAreaGeometry(area, node, ppcm);
        } else {
            const areas = new MPlaza.Areas();
            const area = new MPlaza.Area();
            area.set('areaID', -1);
            area.set('guid', MPlaza.generateUUID());
            area.set('name', node?.name || '');
            ComposerTemplateNodeAdapter.setAreaGeometry(area, node, ppcm);
            areas.add(area);
            side.set('areas', areas);
        }
        return side;
    }

    static saveTemplates(
        templates: ComposerTemplateNode[],
        areas: ComposerAreaNode[]
    ): {
        designs: MPlaza.Designs;
        sides: MPlaza.Sides;
        modelSceneMeshSides: MPlaza.ModelSceneMeshSides;
    } {
        const resultDesigns = new MPlaza.Designs();
        const resultSides = new MPlaza.Sides();
        const resultModelSceneMeshSides = new MPlaza.ModelSceneMeshSides();
        templates.forEach((template) => {
            resultDesigns.push(ComposerTemplateNodeAdapter.fromTemplateNode(template));
        });
        areas.forEach((area) => {
            const { side, modelSides } = ComposerTemplateNodeAdapter.fromAreaNode(area);
            resultSides.push(side);
            modelSides.forEach((meshSide) => {
                resultModelSceneMeshSides.push(meshSide);
            });
        });
        return { designs: resultDesigns, sides: resultSides, modelSceneMeshSides: resultModelSceneMeshSides };
    }
}
